(function () {
  const Core = window.RB.Core;
  const Flow = window.RB.Flow;

  const FINALIZE_STEPS = {
    trigger: ["stepMayPerform", "stepTargets", "stepFinalizeTrigger"],
    ability: ["stepTargets", "stepAbilityCosts", "stepPayment", "stepFinalizeAbility"],
    permanent: ["stepAddCosts", "stepLocation", "stepAccelerate", "stepPayment", "stepEnterBoard"],
    spell: ["stepAddCosts", "stepSpellRiders", "stepRepeat", "stepTargets", "stepPayment", "stepFinalizeSpell"],
  };

  const Play = {
    beginPlay(state, p, iid, opts) {
      opts = opts || {};
      const o = state.objs[iid];
      const c = Core.card(state, iid);
      const fromFacedown = o.zone === "facedown";
      Flow.removeFromZoneLists(state, iid);
      let handIndex = -1;
      if (o.zone === "hand") {
        const h = state.players[p].hand;
        const hi = h.indexOf(iid);
        if (hi >= 0) { h.splice(hi, 1); handIndex = hi; }
      }
      o.zone = "chain";
      o.playFromZone = opts.fromZone || (fromFacedown ? "facedown" : "hand");
      const kind = Core.isType(state, iid, "spell") ? "spell" : "permanent";
      const script = RB.Scripts.forCard(state, iid);
      const item = Flow.addChainItem(state, {
        kind: kind,
        iid: iid,
        controller: p,
        label: c.name,
        program: kind === "spell" ? script.play : null,
        fromFacedown: fromFacedown,
        fromZone: opts.fromZone || (fromFacedown ? "facedown" : "hand"),
        hiddenBf: fromFacedown ? opts.hiddenBf : null,
        handIndex: handIndex,
        flowPlayed: opts.fromZone === "trash" && !opts.viaEffect &&
          !!(Play.flowGrant(state, iid) || (script && script.flowCost)),
        opts: opts,
        openerKind: "play",
      });
      return item;
    },

    rebindClone(x, binds, suffix) {
      if (Array.isArray(x)) {
        return x.map(function (v) { return Play.rebindClone(v, binds, suffix); });
      }
      if (x && typeof x === "object") {
        const out = {};
        for (const k in x) {
          const v = x[k];
          if ((k === "bind" || k === "sameLocAs" || k === "notBind" || k === "whoOf") &&
            typeof v === "string" && binds.indexOf(v) >= 0) {
            out[k] = v + suffix;
          } else {
            out[k] = Play.rebindClone(v, binds, suffix);
          }
        }
        return out;
      }
      if (typeof x === "string" && x.charAt(0) === "$" && binds.indexOf(x.slice(1)) >= 0) {
        return x + suffix;
      }
      return x;
    },

    itemById(state, id) {
      return state.chain.find(function (it) { return it.id === id; });
    },

    stepFinalize(state, frame) {
      const item = Play.itemById(state, frame.chainId);
      if (!item) return "done";
      if (item.status !== "pending") return "done";
      const steps = FINALIZE_STEPS[item.kind];
      if (!steps) throw new Error("finalize: unknown kind " + item.kind);
      for (const step of steps) {
        if (!Play[step](state, frame, item)) return state.pending ? "wait" : "done";
      }
      return "done";
    },

    stepMayPerform(state, frame, item) {
      const a = frame.answers;
      if (!item.optional) return true;
      if (item.exhaustSelf && !Play.exhaustCostPayable(state, item)) {
        Play.removeChainItem(state, item.id);
        return false;
      }
      if (a.perform === undefined && item.exhaustSelf) item.exhaustSelfClaimed = true;
      if (a.perform === undefined) {
        state.pending = {
          kind: "choose", player: item.controller, tag: "may",
          prompt: "Perform: " + item.label + "?", options: ["Yes", "No"],
          frameKey: "perform",
        };
        return false;
      }
      if (a.perform === false) {
        Play.removeChainItem(state, item.id);
        return false;
      }
      return true;
    },

    cohereOk(state, pd, picks) {
      if (!pd || pd.cohere !== "loc" || picks.length < 2) return true;
      const locOf = function (iid) {
        const o = state.objs[iid];
        return o ? o.loc : null;
      };
      const first = locOf(picks[0]);
      if (!first) return false;
      return picks.every(function (iid) {
        const l = locOf(iid);
        return !!l && Core.sameLoc(first, l);
      });
    },

    stepFinalizeTrigger(state, frame, item) {
      item.status = "finalized";
      Play.logChainItem(state, item, "trigger", item.label);
      Play.afterFinalize(state, item);
      return true;
    },

    stepFinalizeAbility(state, frame, item) {
      item.status = "finalized";
      Play.logChainItem(state, item, "activate",
        state.players[item.controller].name + " activates " + item.label +
        (item.abilityText ? " (" + item.abilityText + ")" : ""));
      Play.afterFinalize(state, item);
      return true;
    },

    logChainItem(state, item, kind, lead) {
      const tgts = Core.itemTargets(item);
      Core.log(state, lead + Core.targetClause(state, tgts) + ".", {
        k: kind, p: item.controller,
        iids: [item.iid || item.source].filter(Boolean),
        tgt: tgts,
      });
      item.logMark = state.gameLog.length;
    },

    FROM_ZONE_NOTE: {
      facedown: " from Hidden", trash: " from the trash", banish: " from banishment",
      deck: " from their deck", champion: " from the Champion Zone",
    },

    fromZoneNote(item) {
      return Play.FROM_ZONE_NOTE[item.fromZone] || "";
    },

    stepLocation(state, frame, item) {
      if (!Core.isType(state, item.iid, "unit")) return true;
      const a = frame.answers;
      const bfTok = (RB.Scripts.forCard(state, item.iid) || {}).addsBfToken;
      if (bfTok && a.bfTokenDone === undefined) {
        a.bfTokenDone = true;
        const there = state.battlefields.some(function (b) { return Core.name(state, b) === bfTok.card; });
        if (!there) {
          const nb = Flow.addBattlefield(state, bfTok.card, item.controller);
          if (bfTok.enterThere) a.location = { t: "bf", iid: nb };
        }
      }
      if (a.location === undefined && item.opts && item.opts.forceLoc) a.location = item.opts.forceLoc;
      if (a.location !== undefined) return true;
      const cands = Play.playLocations(state, item);
      if (!cands.length) {
        Play.cancelPlay(state, item);
        return false;
      }
      if (cands.length === 1) {
        a.location = cands[0];
        return true;
      }
      state.pending = {
        kind: "choose", player: item.controller, tag: "location",
        prompt: "Play " + Core.name(state, item.iid) + " where?", cands: cands, min: 1, max: 1,
        frameKey: "location", locChoices: true,
      };
      return false;
    },

    stepAccelerate(state, frame, item) {
      const a = frame.answers;
      if (a.accelerate !== undefined) return true;
      if (!Core.isType(state, item.iid, "unit") || !Core.hasKw(state, item.iid, "accelerate") || item.fromFacedown) return true;
      const withAcc = Play.effectiveCost(state, item, Object.assign({}, a, { accelerate: true }));
      if (Play.poolPotentialCovers(state, item.controller, withAcc, Play.payCtxForItem(state, item))) {
        state.pending = {
          kind: "choose", player: item.controller, tag: "may",
          prompt: "Pay Accelerate cost (+1 energy, +1 power) to enter ready?",
          options: ["Yes", "No"], frameKey: "accelerate",
        };
        return false;
      }
      a.accelerate = false;
      return true;
    },

    stepEnterBoard(state, frame, item) {
      Play.enterBoard(state, item, frame.answers);
      return true;
    },

    stepSpellRiders(state, frame, item) {
      if (item.costsSettled || item.ridersAppended) return true;
      item.ridersAppended = true;
      Play.appendAddCostRidersToSpell(state, item, frame.answers);
      return true;
    },

    stepFinalizeSpell(state, frame, item) {
      const settled = !!item.costsSettled;
      item.costsSettled = true;
      item.status = "finalized";
      if (!settled) {
        item.playMods = Play.nextPlayModsFor(state, item.controller, item.iid, "bonusDamage");
        Play.consumeNextPlayMods(state, item.controller, item.iid);
        state.turnFacts.players[item.controller].played += 1;
        state.turnFacts.players[item.controller].spells += 1;
        Play.logChainItem(state, item, "play",
          state.players[item.controller].name + " plays " + Core.name(state, item.iid) + Play.fromZoneNote(item));
        RB.FX.emit(state, { type: "finalizedCard", iid: item.iid, player: item.controller, fromFacedown: item.fromFacedown ? true : undefined });
      }
      Play.afterFinalize(state, item);
      return true;
    },

    putIntoPlay(state, p, iid, opts) {
      const o = state.objs[iid];
      if (!o) return null;
      Flow.removeFromZoneLists(state, iid);
      const item = Flow.addChainItem(state, {
        kind: "permanent", iid: iid, controller: p, label: Core.name(state, iid),
        program: null, fromZone: o.zone, opts: opts || {}, openerKind: "play",
      });
      o.zone = "chain";
      Play.enterBoard(state, item, { location: { t: "base", p: p } });
      return iid;
    },

    probeItem(state, p, iid, origin) {
      origin = origin || {};
      return {
        kind: Core.isType(state, iid, "spell") ? "spell" : "permanent",
        iid: iid, controller: p, targets: {}, vars: {}, ctx: {}, opts: {},
        label: Core.name(state, iid),
        flowPlayed: origin.fromZone === "trash",
        fromFacedown: origin.fromZone === "facedown",
        hiddenBf: origin.hiddenBf || null,
      };
    },

    afterFinalize(state, item) {
      if (item.kind !== "trigger" && state.showdown && state.showdown.stage === "open") {
        state.showdown.passes = 0;
      }
      const pendingLeft = state.chain.some(function (it) { return it.status === "pending"; });
      if (item.kind === "ability" && item.isAdd) {
        Play.removeChainItem(state, item.id);
        state.tasks.push({ k: "resolve", chainId: item.id, direct: item });
        return;
      }
      if (!pendingLeft) {
        state.priority = state.chain.length ? state.chain[state.chain.length - 1].controller : null;
        state.passCount = 0;
      }
    },

    stepTargets(state, frame, item) {
      let specs = item.targetSpecs || [];
      if ((item.kind === "spell" || item.kind === "permanent") && item.iid) {
        const script = RB.Scripts.forCard(state, item.iid);
        if (script && script.targets && script.targets.length) specs = script.targets;
      }
      if (item.extraTargetSpecs && item.extraTargetSpecs.length) specs = specs.concat(item.extraTargetSpecs);
      const a = frame.answers;
      a.targetIdx = a.targetIdx || 0;
      while (a.targetIdx < specs.length) {
        const spec = specs[a.targetIdx];
        if (item.targets[spec.bind] !== undefined) { a.targetIdx++; continue; }
        const cands = Play.chainNarrowedCandidates(state, specs, a.targetIdx, item, Play.candidates(state, spec.spec || spec, item));
        const min = spec.min !== undefined ? spec.min : 1;
        if (cands.length < min && min > 0) {
          if (item.kind === "trigger") {
            Play.removeChainItem(state, item.id);
            RB.FX.opLog(state, item, "target", item.label + ": no legal choices, removed.");
            return false;
          }
          if (item.kind === "spell") {
            RB.FX.opLog(state, item, "target", item.label + " cannot be played: no legal target.");
            Play.cancelPlay(state, item);
            return false;
          }
          item.targets[spec.bind] = [];
          a.targetIdx++;
          continue;
        }
        const fromHidden = item.fromFacedown || (item.ctx && item.ctx.fromFacedown);
        const hiddenBf = item.hiddenBf || (item.ctx && item.ctx.hiddenBf);
        const boardSpec = ((spec.spec || spec).zone || "board") === "board";
        state.pending = {
          kind: "choose", player: item.controller, tag: "target",
          prompt: spec.prompt || ("Choose " + (spec.what || "target") + " for " + item.label),
          src: item.label, srcIid: item.iid,
          step: specs.length > 1 ? { n: a.targetIdx + 1, of: specs.length } : undefined,
          cands: cands, min: min, max: spec.max !== undefined ? spec.max : 1,
          bind: spec.bind,
          cancellable: item.kind === "spell" || item.kind === "ability",
          cohere: spec.cohereLoc ? "loc" : undefined,
          hint: fromHidden && hiddenBf && boardSpec
            ? "Played from Hidden - rule 811.1.d.2 restricts the choice to " + Core.name(state, hiddenBf) + "."
            : undefined,
        };
        return false;
      }
      return true;
    },

    enterBoard(state, item, answers) {
      const iid = item.iid;
      const o = state.objs[iid];
      Play.removeChainItem(state, item.id);
      const isUnit = Core.isType(state, iid, "unit");
      o.zone = "board";
      o.controller = item.controller;
      o.enteredTurn = state.turn.num;
      if (isUnit) {
        const script = RB.Scripts.forCard(state, iid);
        o.loc = answers.location || { t: "base", p: item.controller };
        const ctl = item.controller;
        o.ready = !!answers.accelerate || !!(item.opts && item.opts.enterReady)
          || Play.addCostEntries(state, item).some(function (e, i) { return e.entersReady && answers["addCost" + i] === true; })
          || !!(script && script.entersReady)
          || !!(script && script.entersReadyCond &&
            RB.Scripts.staticCond(state, iid, item.controller, script.entersReadyCond))
          || state.turnFacts.players[item.controller].unitsEnterReady
          || Play.nextPlayModsFor(state, ctl, iid, "entersReady").length > 0
          || Core.ruleSources(state, "auraEntersReady", { legends: true, controller: ctl }).some(function (s) { return s.iid !== iid; })
          || Core.ruleSources(state, "auraEntersReadyCond", { legends: true, controller: ctl }).some(function (s) {
            return s.iid !== iid && RB.Scripts.staticCond(state, s.iid, ctl, s.rule);
          });
        if (o.loc.t === "bf") RB.Flow.markContested(state, o.loc.iid, o.controller);
      } else {
        o.loc = item.fromFacedown && item.hiddenBf ? { t: "bf", iid: item.hiddenBf } : { t: "base", p: item.controller };
        const gearScript = RB.Scripts.forCard(state, item.iid);
        o.ready = !(gearScript && gearScript.entersExhausted);
        const host = item.opts && item.opts.attachTo && state.objs[item.opts.attachTo];
        if (host && host.zone === "board" && Core.isType(state, iid, "gear")) RB.Flow.attach(state, iid, item.opts.attachTo);
      }
      state.turnFacts.players[item.controller].played += 1;
      if (Core.isType(state, iid, "gear") && !o.token) state.turnFacts.players[item.controller].gearPlayed += 1;
      if (Core.isType(state, iid, "unit") && !o.token) state.turnFacts.players[item.controller].nonTokenUnitsPlayed += 1;
      if (state.showdown && state.showdown.stage === "open") state.showdown.passes = 0;
      Core.log(state, state.players[item.controller].name + " plays " + Core.name(state, iid) + Play.fromZoneNote(item) +
        (o.loc.t === "bf" ? " at " + Core.name(state, o.loc.iid) : "") + ".", {
        k: "play", p: item.controller, iids: [iid].concat(o.loc.t === "bf" ? [o.loc.iid] : []),
      });
      RB.FX.emit(state, { type: "finalizedCard", iid: iid, player: item.controller });
      RB.FX.emit(state, { type: "played", iid: iid, player: item.controller, loc: o.loc, fromFacedown: !!item.fromFacedown });
      RB.FX.emitPlayTriggers(state, iid, item);
      Play.queueAddCostRiders(state, item, answers);
      Play.consumeNextPlayMods(state, item.controller, iid);
      const playedScript = RB.Scripts.forCard(state, iid);
      if (playedScript && (playedScript.abilities || []).some(function (ab) { return ab.label === "Equip"; })) {
        state.turnFacts.players[item.controller].equipmentPlayed = true;
      }
      state.needCleanup = true;
    },

    cancelPlay(state, item) {
      Play.removeChainItem(state, item.id);
      if (item.iid) {
        const o = state.objs[item.iid];
        if (item.fromZone === "facedown" && item.hiddenBf) {
          o.zone = "facedown";
          Flow.setHidden(state, item.hiddenBf, item.iid);
        } else if (item.fromZone === "trash") {
          o.zone = "trash";
          state.players[o.owner].trash.push(item.iid);
        } else if (item.fromZone === "champion") {
          o.zone = "champion";
          state.players[o.owner].champion.push(item.iid);
        } else {
          o.zone = "hand";
          state.players[o.owner].hand.push(item.iid);
        }
      }
      Core.log(state, item.label + " play cancelled.", {
        k: "play", p: item.controller, iids: [item.iid].filter(Boolean),
      });
      state.needCleanup = true;
    },

    removeChainItem(state, id) {
      const idx = state.chain.findIndex(function (it) { return it.id === id; });
      if (idx >= 0) state.chain.splice(idx, 1);
      state.needCleanup = true;
    },
  };

  window.RB.Play = Play;
})();
